import { z } from 'zod'
import { ApprovalDecisionSchema, RiskClassSchema, RunStatusSchema, StepTypeSchema, TrustLabelSchema } from './enums.ts'
import { MessageParamsSchema } from './messages/kinds.ts'

/**
 * The wire protocol between the agent runtime and every client.
 *
 * Two properties make this resumable rather than merely "streamy":
 *
 *  1. `seq` is a per-run monotonic integer assigned at persist time, not at send time.
 *     A client that drops mid-stream reconnects with `?after=<lastSeq>` and the server
 *     replays from the durable event log. Without a server-assigned sequence there is no
 *     way to distinguish "you missed events" from "nothing happened".
 *  2. Every event is persisted BEFORE it is flushed to the socket. A client can therefore
 *     never observe an event the server would not replay -- the failure mode that turns a
 *     reconnect into a silently truncated conversation.
 */
export const StreamEventBaseSchema = z.object({
  seq: z.number().int().nonnegative(),
  ts: z.iso.datetime(),
  runId: z.uuid()
})

const withBase = <T extends z.ZodRawShape>(shape: T) => StreamEventBaseSchema.extend(shape)

/** Keeps proxies and socket-idle timers alive on long tool/model turns. Never persisted. */
export const HeartbeatEventSchema = withBase({
  type: z.literal('heartbeat')
})

export const RunStartedEventSchema = withBase({
  type: z.literal('run.started'),
  conversationId: z.uuid(),
  userMessageId: z.uuid(),
  assistantMessageId: z.uuid(),
  budget: z.object({
    maxSteps: z.number().int().positive(),
    maxModelCalls: z.number().int().positive(),
    maxToolCalls: z.number().int().positive(),
    maxCostUsd: z.number().nonnegative(),
    maxWallClockMs: z.number().int().positive()
  })
})

export const RunStatusEventSchema = withBase({
  type: z.literal('run.status'),
  status: RunStatusSchema,
  reason: z.string().nullish()
})

/**
 * What a step's caption IS, rather than what it says (PRD-27 D-8, LOC-042).
 *
 * `run_steps.label` is English prose the orchestrator writes -- "Reasoning with balanced", a
 * tool's display name -- and it is simultaneously the most-watched text in the product while
 * an answer streams and a durable record an auditor reads. It stays, permanently: a row
 * written before `0014` has no code and renders from it, and an auditor reads it without
 * needing a catalogue.
 *
 * The code and its parameters are what let a browser render that same step in the reader's
 * language. A parameter is a VALUE -- a model alias, a tool's own name -- and never a phrase,
 * because Portuguese cannot agree with a pre-built English one (D-20).
 *
 * The sentences are deliberately not here. They live in `apps/web/src/i18n/messages/<locale>/`
 * with all the other copy, in BOTH registers: the operator's wording in `trace` and the
 * reader's in `chat`, which say different things about the same step on purpose (D-5 amended,
 * AGM-022).
 */
export const StepLabelCodeSchema = z.enum([
  /** A provider call. The model alias is data and is never translated (D-17). */
  'model_call',
  'knowledge_search',
  'memory_write',
  'announcement',
  'delegation',
  /**
   * Any other tool, which today means every MCP capability. Its display name is written by a
   * server this platform does not own, so it is DATA (D-17): it travels as a parameter and is
   * rendered verbatim in both languages rather than earning a code and a sentence of its own.
   */
  'tool_call'
])
export type StepLabelCode = z.infer<typeof StepLabelCodeSchema>

/**
 * What each code needs in order to be rendered -- as runtime schemas, not as types alone.
 *
 * The emit site is typed off these, and the CLIENT parses what it was sent through the same
 * schema before handing it to a catalogue entry. That is what makes the fallback honest: a
 * payload from a server this client is not paired with degrades to the stored English `label`
 * rather than to "Reasoning with undefined".
 */
export const StepLabelParamsByCode = {
  model_call: z.object({ alias: z.string() }),
  knowledge_search: z.object({}),
  memory_write: z.object({}),
  announcement: z.object({}),
  delegation: z.object({}),
  tool_call: z.object({ tool: z.string() })
} satisfies Record<StepLabelCode, z.ZodType>

export type StepLabelParams = { [K in StepLabelCode]: z.infer<(typeof StepLabelParamsByCode)[K]> }

/**
 * A caption as the orchestrator hands it over.
 *
 * Written as a union of per-code members rather than as `{ code: StepLabelCode; params: ... }`,
 * for the reason `EmittableEvent` is a union below: collapsing it would accept a `tool` on a
 * `model_call`, and the point of keying a caption is that the code and its values stay checked
 * together.
 */
export type StepCaption = { [K in StepLabelCode]: { code: K; params: StepLabelParams[K] } }[StepLabelCode]

/**
 * The catalogue's side of the same contract: one whole sentence per code, per locale, built
 * from the values rather than concatenated from fragments (LOC-013).
 *
 * A locale's map is `satisfies StepCaptions`, so a new code is a compile error in the catalogue
 * rather than a blank on the panel the product is named for.
 */
export type StepCaptions = { [K in StepLabelCode]: (params: StepLabelParams[K]) => string }

/**
 * The wire shape of a caption's parameters. Narrow for the same reason `MessageParams` is: a
 * parameter is a value to interpolate, never structured data a client has to know how to read.
 */
export const StepLabelParamsSchema = MessageParamsSchema

export const StepStartedEventSchema = withBase({
  type: z.literal('step.started'),
  stepId: z.uuid(),
  stepType: StepTypeSchema,
  index: z.number().int().nonnegative(),
  /** The server's English. Never removed -- it is the operator's line and the client's fallback. */
  label: z.string(),
  /**
   * The same caption, named rather than spelled (D-8).
   *
   * Optional, and a plain string rather than `StepLabelCodeSchema`, for exactly the reason
   * `ApiIssue.messageKey` is: a server that has learned a new code must not break a client
   * that has not. That client falls back to `label`, which is why `label` is on the wire and
   * stays there. Strictness belongs at the two ends -- typed at the emit site, exhaustive in
   * the catalogue -- not in the middle, where it would turn a deploy-order problem into an
   * outage.
   */
  labelCode: z.string().nullish(),
  labelParams: StepLabelParamsSchema.nullish()
})

export const StepCompletedEventSchema = withBase({
  type: z.literal('step.completed'),
  stepId: z.uuid(),
  durationMs: z.number().int().nonnegative()
})

export const StepFailedEventSchema = withBase({
  type: z.literal('step.failed'),
  stepId: z.uuid(),
  durationMs: z.number().int().nonnegative(),
  code: z.string(),
  message: z.string(),
  retryable: z.boolean()
})

/**
 * Emitted before a provider call. This is the payload that makes the UI's "AI call"
 * inspector honest: the caller sees which alias resolved to which concrete model, why
 * that route was chosen, and exactly which labelled segments were assembled into the
 * context -- with sizes, not contents, so an operator watching a stream cannot read a
 * colleague's private documents over their shoulder.
 */
export const ModelRequestEventSchema = withBase({
  type: z.literal('model.request'),
  stepId: z.uuid(),
  alias: z.string(),
  provider: z.string(),
  model: z.string(),
  routingReason: z.string(),
  temperature: z.number().nullish(),
  maxOutputTokens: z.number().int().positive(),
  toolsOffered: z.array(z.string()),
  segments: z.array(
    z.object({
      label: TrustLabelSchema,
      title: z.string(),
      chars: z.number().int().nonnegative(),
      estimatedTokens: z.number().int().nonnegative()
    })
  ),
  estimatedInputTokens: z.number().int().nonnegative()
})

export const ModelDeltaEventSchema = withBase({
  type: z.literal('model.delta'),
  stepId: z.uuid(),
  text: z.string()
})

/** Extended-thinking deltas, surfaced as a collapsed lane -- never mixed into the answer body. */
export const ModelThinkingDeltaEventSchema = withBase({
  type: z.literal('model.thinking_delta'),
  stepId: z.uuid(),
  text: z.string()
})

/**
 * The provider call is being retried; discard everything streamed for this step so far.
 *
 * A retried call re-streams from the beginning, so without this the deltas of the failed
 * attempt and the successful one concatenate. The live view self-corrects when
 * `message.completed` arrives, but the DURABLE log keeps the duplicated prefix forever --
 * and a replayed trace is supposed to be indistinguishable from the one watched live.
 */
export const ModelResetEventSchema = withBase({
  type: z.literal('model.reset'),
  stepId: z.uuid(),
  attempt: z.number().int().positive(),
  reason: z.string()
})

export const ModelUsageEventSchema = withBase({
  type: z.literal('model.usage'),
  stepId: z.uuid(),
  provider: z.string(),
  model: z.string(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  cacheReadTokens: z.number().int().nonnegative(),
  cacheWriteTokens: z.number().int().nonnegative(),
  costUsd: z.number().nonnegative(),
  timeToFirstTokenMs: z.number().int().nonnegative().nullish(),
  /**
   * Milliseconds from request start until the first ANSWER (text) delta.
   *
   * `timeToFirstTokenMs` counts the first token of ANY kind, and a reasoning model streams
   * its thinking first — so that number alone cannot say when the model stopped deliberating
   * and started writing. This one can: everything before it is "thinking" on the provenance
   * tape, everything after is "writing". Null when the call produced no text at all (a pure
   * tool-use step) — and `nullish`, not required, so replays of events persisted before this
   * field existed still parse; the tape falls back to a single undivided segment for them.
   */
  timeToFirstTextMs: z.number().int().nonnegative().nullish(),
  latencyMs: z.number().int().nonnegative(),
  finishReason: z.string(),
  providerRequestId: z.string().nullish()
})

export const RetrievalStartedEventSchema = withBase({
  type: z.literal('retrieval.started'),
  stepId: z.uuid(),
  query: z.string(),
  strategy: z.string(),
  scopeIds: z.array(z.uuid())
})

export const RetrievalCompletedEventSchema = withBase({
  type: z.literal('retrieval.completed'),
  stepId: z.uuid(),
  latencyMs: z.number().int().nonnegative(),
  lexicalCandidates: z.number().int().nonnegative(),
  semanticCandidates: z.number().int().nonnegative(),
  fusedCandidates: z.number().int().nonnegative(),
  /** Candidates dropped because the caller is not authorized. Non-zero here is a working ACL filter. */
  authorizationFiltered: z.number().int().nonnegative(),
  selected: z.number().int().nonnegative(),
  results: z.array(
    z.object({
      chunkId: z.uuid(),
      documentId: z.uuid(),
      documentTitle: z.string(),
      sourceName: z.string(),
      locator: z.string(),
      score: z.number(),
      lexicalRank: z.number().int().nullish(),
      semanticRank: z.number().int().nullish(),
      snippet: z.string()
    })
  )
})

export const ToolProposedEventSchema = withBase({
  type: z.literal('tool.proposed'),
  stepId: z.uuid(),
  toolCallId: z.string(),
  tool: z.string(),
  displayName: z.string(),
  risk: RiskClassSchema,
  argumentsSummary: z.string(),
  arguments: z.record(z.string(), z.unknown())
})

/**
 * The run has parked. `digest` binds the approval to the exact normalized arguments that
 * were shown to the approver: if anything about the proposed action changes, the digest
 * changes and the stale decision no longer applies (PRD-04 MCP-018).
 */
export const ApprovalRequiredEventSchema = withBase({
  type: z.literal('approval.required'),
  stepId: z.uuid(),
  approvalId: z.uuid(),
  toolCallId: z.string(),
  tool: z.string(),
  displayName: z.string(),
  risk: RiskClassSchema,
  digest: z.string(),
  arguments: z.record(z.string(), z.unknown()),
  rationale: z.string(),
  reversible: z.boolean(),
  dataLeavingPlatform: z.array(z.string()),
  expiresAt: z.iso.datetime()
})

export const ApprovalResolvedEventSchema = withBase({
  type: z.literal('approval.resolved'),
  approvalId: z.uuid(),
  decision: ApprovalDecisionSchema,
  decidedBy: z.string(),
  decidedAt: z.iso.datetime(),
  note: z.string().nullish()
})

export const ToolStartedEventSchema = withBase({
  type: z.literal('tool.started'),
  stepId: z.uuid(),
  toolCallId: z.string(),
  tool: z.string()
})

export const ToolCompletedEventSchema = withBase({
  type: z.literal('tool.completed'),
  stepId: z.uuid(),
  toolCallId: z.string(),
  tool: z.string(),
  latencyMs: z.number().int().nonnegative(),
  resultSummary: z.string(),
  resultPreview: z.string()
})

export const ToolFailedEventSchema = withBase({
  type: z.literal('tool.failed'),
  stepId: z.uuid(),
  toolCallId: z.string(),
  tool: z.string(),
  latencyMs: z.number().int().nonnegative(),
  code: z.string(),
  message: z.string()
})

export const CitationSchema = z.object({
  id: z.uuid(),
  marker: z.number().int().positive(),
  chunkId: z.uuid(),
  documentId: z.uuid(),
  documentTitle: z.string(),
  sourceId: z.uuid(),
  sourceName: z.string(),
  locator: z.string(),
  snippet: z.string(),
  score: z.number(),
  /**
   * False when the cited span could not be shown to support the surrounding claim.
   * Displayed, not silently dropped -- a model that cites loosely is a signal the
   * operator needs, and hiding it manufactures false confidence (PRD-07 KB-016).
   */
  supported: z.boolean(),
  freshness: z.object({
    documentUpdatedAt: z.iso.datetime().nullish(),
    indexedAt: z.iso.datetime(),
    stale: z.boolean()
  })
})
export type Citation = z.infer<typeof CitationSchema>

export const CitationsEventSchema = withBase({
  type: z.literal('citations'),
  citations: z.array(CitationSchema)
})

export const MessageCompletedEventSchema = withBase({
  type: z.literal('message.completed'),
  messageId: z.uuid(),
  content: z.string(),
  citations: z.array(CitationSchema),
  /** True when saved preferences entered this run's context (PRD-18 AGM-092): the reader
   *  is told memory shaped the answer, and the UI hangs inspect/forget off this flag. */
  memoryInfluenced: z.boolean().optional()
})

/**
 * A structured close-out (PRD-03 RUN-016). `completedEffects` / `failedEffects` exist so a
 * partially-failed run cannot present itself as a success: the UI renders exactly what
 * landed and what did not.
 */
export const RunOutcomeSchema = z.object({
  status: RunStatusSchema,
  summary: z.string(),
  completedEffects: z.array(z.string()),
  failedEffects: z.array(z.string()),
  unresolvedUncertainty: z.array(z.string()),
  recommendedNextAction: z.string().nullish(),
  totals: z.object({
    steps: z.number().int().nonnegative(),
    modelCalls: z.number().int().nonnegative(),
    toolCalls: z.number().int().nonnegative(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative(),
    durationMs: z.number().int().nonnegative()
  })
})
export type RunOutcome = z.infer<typeof RunOutcomeSchema>

export const RunCompletedEventSchema = withBase({
  type: z.literal('run.completed'),
  outcome: RunOutcomeSchema
})

export const RunFailedEventSchema = withBase({
  type: z.literal('run.failed'),
  code: z.string(),
  message: z.string(),
  retryable: z.boolean(),
  outcome: RunOutcomeSchema
})

export const BudgetWarningEventSchema = withBase({
  type: z.literal('budget.warning'),
  dimension: z.enum(['steps', 'model_calls', 'tool_calls', 'cost_usd', 'wall_clock_ms']),
  used: z.number(),
  limit: z.number()
})

export const StreamEventSchema = z.discriminatedUnion('type', [
  HeartbeatEventSchema,
  RunStartedEventSchema,
  RunStatusEventSchema,
  StepStartedEventSchema,
  StepCompletedEventSchema,
  StepFailedEventSchema,
  ModelRequestEventSchema,
  ModelDeltaEventSchema,
  ModelThinkingDeltaEventSchema,
  ModelResetEventSchema,
  ModelUsageEventSchema,
  RetrievalStartedEventSchema,
  RetrievalCompletedEventSchema,
  ToolProposedEventSchema,
  ApprovalRequiredEventSchema,
  ApprovalResolvedEventSchema,
  ToolStartedEventSchema,
  ToolCompletedEventSchema,
  ToolFailedEventSchema,
  CitationsEventSchema,
  MessageCompletedEventSchema,
  RunCompletedEventSchema,
  RunFailedEventSchema,
  BudgetWarningEventSchema
])

export type StreamEvent = z.infer<typeof StreamEventSchema>
export type StreamEventType = StreamEvent['type']

/** Narrowing helper so consumers get exhaustive switch support without casts. */
export type StreamEventOf<T extends StreamEventType> = Extract<StreamEvent, { type: T }>

/**
 * An event as a producer writes it: everything except the three fields the emitter stamps.
 *
 * Built by mapping over the union member-by-member rather than `Omit<StreamEvent, ...>`,
 * which would collapse into a single object type and silently accept a `stepId` on a
 * `run.completed`. Keeping it a discriminated union preserves excess-property checking, so
 * a malformed event is a compile error at the call site instead of a schema failure in the
 * browser.
 */
export type EmittableEvent = {
  [K in StreamEventType]: Omit<StreamEventOf<K>, 'seq' | 'ts' | 'runId'>
}[StreamEventType]

/**
 * Heartbeats are transport keep-alive, not history: replaying them on reconnect would
 * pad the log without conveying anything. Everything else is durable.
 */
export const isPersistedEvent = (type: StreamEventType): boolean => type !== 'heartbeat'
